import axios from 'axios';
import { api } from './api';

export interface ComboData {
  file: File;
}

export interface ComboListItem {
  id: string;
  data: {
    created_at: string;
    description: string;
    image_url: string;
    name: string;
    start_date: string;
    template_id: string;
    combo_id: string;
    title: string;
    thumbnail_url: string;
  };
}

export interface ProcessedComboListItem {
  id: number;
  created_at: string;
  updated_at: string;
  combos_count: 0 | 1;
  manual_combos_count: 0 | 1;
  name: string;
}

export interface ProcessedComboByIdListItem {
  id: number;
  data: {
    combo_id: string;
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    template_id: number;
    image_url: string;
    thumbnail_url: string;
    created_at: string;
    country: string;
  }

}

export interface BulkJobStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_images: number;
  processed_images: number;
  successful_images: number;
  failed_images: number;
  progress_percentage: number;
  is_complete: boolean;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface ComboListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ComboListItem[];
}

export interface ComboListParams {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
  ordering?: string;
}

export interface ProcessedComboListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProcessedComboListItem[];
}

export interface ProcessedComboByIdResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProcessedComboByIdListItem[];
}

export const combosService = {

  getCombosList: async (params?: ComboListParams): Promise<ComboListResponse> => {
    try {
      const response = await api.get('/combos/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de combos:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener la lista de combos');
    }
  },

  saveCombo: async (comboData: ComboData): Promise<{ success: boolean; message: string }> => {
    try {
      const formData = new FormData();
      formData.append('file', comboData.file);

      const response = await api.post('/combos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al guardar combo:', error);

      if (axios.isAxiosError(error)) {
        return {
          success: false,
          message: error.response?.data?.error || 'Error al comunicarse con el servidor'
        };
      }

      return {
        success: false,
        message: 'Error inesperado al guardar el combo'
      };
    }
  },

  downloadImages: async (comboId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.get(`/combos/${comboId}/download-images`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al descargar imágenes:', error);
      return {
        success: false,
        message: 'Error al descargar imágenes'
      };
    }
  },

  getProcessedCombos: async (params?: ComboListParams): Promise<ProcessedComboListResponse> => {
    try {
      const response = await api.get('/process-images', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener lista de combos procesados:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener la lista de combos procesados');
    }
  },

  getProcessedCombosById: async (id: number, type: 'manual' | 'bus', params?: ComboListParams): Promise<ProcessedComboByIdResponse> => {
    try {
      const response = await api.get(`/process-images/${id}/${type === 'manual' ? 'manual-combos' : 'combos'}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener combo procesado:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener el combo procesado');
    }
  },

  downloadGroupedCombos: async (comboIds: string[], isFolder?: boolean, isManual?: boolean): Promise<{ task_id: string; status_url: string }> => {
    try {
      const params = isManual ? { manual_combo_ids: comboIds } : { combo_ids: comboIds };
      const response = await api.post(`/process-images/download-zip${isFolder ? '?type=folder' : ''}`,
        {
          ...params
        }
      );

      return {
        task_id: response.data.task_id,
        status_url: response.data.status_url
      };
    } catch (error) {
      console.error('Error al descargar combos:', error);
      return {
        task_id: '',
        status_url: '',
      };
    }
  },
  getStatusCombos: async (status_url:string): Promise<{ status: 'success' | 'pending' | 'started' |'error'; url: string }> => {
    try {
      const response = await axios.get(status_url);

      return {
        status: response.data.status,
        url: response.data.url
      };
    } catch (error) {
      console.error('Error al descargar combos:', error);
      return {
        status: 'error',
        url: '',
      };
    }
  },

  getBulkJobStatus: async (processImageId: number): Promise<BulkJobStatus> => {
    try {
      const response = await api.get(`/images/bulk-jobs/by-process-image/${processImageId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estado del proceso de imágenes:', error);

      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Error al comunicarse con el servidor');
      }

      throw new Error('Error inesperado al obtener el estado del proceso');
    }
  }
};
